import { forwardRef } from 'react';
import { cn } from '../../lib/utils';

/**
 * Input component matching design system specifications
 * Replaces hardcoded input classes with design tokens
 * 
 * Variants:
 * - standard: Default input with full border (default)
 * - borderless: Transparent background with bottom border only (for invoice number, dates, reference)
 * - table-number: For table number inputs (no spinner, transparent background)
 * 
 * All variants use text-xs (12px) to comply with typography constraint.
 */
export const Input = forwardRef(({ className, type, label, requiredIndicator, id, variant = 'standard', ...props }, ref) => {
  const inputId = id || `input-${props.name || Math.random().toString(36).substr(2, 9)}`;
  
  // Determine variant based on type if variant not explicitly set
  // Date inputs should use borderless variant by default
  const effectiveVariant = variant === 'standard' && type === 'date' ? 'borderless' : variant;
  
  // Variant-specific classes
  const variantClasses = {
    standard: cn(
      'flex h-9 w-full',
      'bg-[var(--ds-control-field-bg)]',
      'border border-[var(--ds-control-field-border)]',
      'rounded-[var(--ds-radius-md)]',
      'px-3 py-2',
    ),
    borderless: cn(
      'flex h-8 w-full',
      'bg-transparent',
      'border-0 border-b border-gray-300 dark:border-gray-600',
      'rounded-none',
      'px-2 py-1',
      'focus:border-blue-500 dark:focus:border-blue-400',
      'focus:outline-none focus:ring-0',
      'text-gray-900 dark:text-gray-100',
    ),
    'table-number': cn(
      'flex w-full',
      'bg-transparent',
      'border-none',
      'rounded-none',
      'px-0 py-1',
      'text-right',
      'no-spinner',
      'focus:outline-none focus:ring-0',
      'text-gray-900 dark:text-gray-100',
    ),
  };
  
  // Base classes common to all variants
  // All text must be 12px (text-xs) per typography constraint
  const baseClasses = cn(
    'text-xs', // 12px - complies with typography constraint
    'text-[var(--ds-text-primary)]',
    'placeholder:text-[var(--ds-text-muted)]',
    'disabled:cursor-not-allowed disabled:opacity-50',
    'disabled:text-gray-400 dark:disabled:text-gray-500',
    'transition-colors duration-180',
  );
  
  // Focus classes - only for standard variant
  const focusClasses = effectiveVariant === 'standard'
    ? 'focus:ring-2 focus:ring-[var(--ds-accent-focus-ring)] focus:border-[var(--ds-accent-primary)]'
    : '';
  
  return (
    <div className="w-full">
      {label && (
        <label 
          htmlFor={inputId}
          className="block text-meta font-medium text-gray-700 dark:text-gray-300 mb-1"
        >
          {label}
          {requiredIndicator && props.required && (
            <span className="text-[var(--ds-status-danger)] ml-1">*</span>
          )}
        </label>
      )}
      <input
        id={inputId}
        type={type}
        className={cn(
          variantClasses[effectiveVariant] || variantClasses.standard,
          baseClasses,
          focusClasses,
          className
        )}
        ref={ref}
        {...props}
      />
    </div>
  );
});

Input.displayName = 'Input';

